/**
 * Static prototype application service — backed by the in-memory mock store.
 * Public method signatures match the original API client.
 */

import {
  FormTemplateWithSections,
  SectionBasedApplication,
  SectionStatus,
  ApplicationWithEvaluations,
} from '../types';
import { db, delay, newId, persist } from '../mocks/store';
import {
  findApp,
  toApplicationWithEvaluations,
  toSectionBasedApplication,
} from '../mocks/mappers';
import authService from './authService';

// Custom error class for application service operations
class ApplicationServiceError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = 'ApplicationServiceError';
  }
}

const currentUserId = (): string =>
  authService.getCurrentUser()?.id ?? '11111111-1111-4111-8111-000000000001';

class ApplicationService {
  async getMyApplications(
    _page: number = 1,
    _limit: number = 50
  ): Promise<{ data: SectionBasedApplication[] }> {
    const userId = currentUserId();
    const data = db.applications
      .filter((a) => a.userId === userId)
      .map(toSectionBasedApplication);
    return delay({ data });
  }

  /**
   * Get active form templates
   */
  async getActiveTemplates(): Promise<FormTemplateWithSections[]> {
    const now = new Date();
    const templates = db.formTemplates
      .filter(
        (t) =>
          t.isActive &&
          (t.isPublished ?? true) &&
          new Date(t.deadline) > now
      )
      .map((t) => ({
        id: t.id,
        name: t.name,
        description: t.description,
        isActive: t.isActive,
        isPublished: t.isPublished ?? true,
        sections: t.sections,
        deadline: t.deadline,
        year: t.year ?? null,
      })) as FormTemplateWithSections[];
    return delay(templates);
  }

  async getClosedTemplates(): Promise<FormTemplateWithSections[]> {
    const now = new Date();
    const templates = db.formTemplates
      .filter((t) => !t.isActive || new Date(t.deadline) < now)
      .sort(
        (a, b) =>
          new Date(b.deadline).getTime() - new Date(a.deadline).getTime()
      )
      .map((t) => ({
        id: t.id,
        name: t.name,
        description: t.description,
        isActive: t.isActive,
        sections: t.sections,
        deadline: t.deadline,
        year: t.year ?? null,
      })) as FormTemplateWithSections[];
    return delay(templates);
  }

  /**
   * Create a new application
   */
  async createApplication(
    formTemplateId: string
  ): Promise<SectionBasedApplication> {
    const userId = currentUserId();
    const count = db.applications.length + 1;
    const ts = new Date().toISOString();
    const template = db.formTemplates.find((t) => t.id === formTemplateId);
    const year = template?.year ?? new Date().getFullYear();
    const app = {
      id: newId('app'),
      userId,
      formTemplateId,
      applicationNumber: `AV${String(year).slice(-2)}${String(count).padStart(3, '0')}`,
      status: 'draft' as const,
      sectionAnswers: {},
      sectionStatus: {},
      lastSaved: ts,
      complianceConfirmed: false,
      submittedAt: null,
      createdAt: ts,
      updatedAt: ts,
      awardNotifiedAt: null,
      statusHistory: [],
    };
    db.applications.push(app);
    persist();
    return delay(toSectionBasedApplication(app), 200);
  }

  async createApplicationFromSource(
    formTemplateId: string,
    sourceId: string
  ): Promise<SectionBasedApplication> {
    const userId = currentUserId();
    const count = db.applications.length + 1;
    const ts = new Date().toISOString();
    const template = db.formTemplates.find((t) => t.id === formTemplateId);
    const year = template?.year ?? new Date().getFullYear();
    const source = db.applications.find((a) => a.id === sourceId);
    const app = {
      id: newId('app'),
      userId,
      formTemplateId,
      applicationNumber: `AV${String(year).slice(-2)}${String(count).padStart(3, '0')}`,
      status: 'draft' as const,
      sectionAnswers: source ? { ...source.sectionAnswers } : {},
      sectionStatus: {},
      lastSaved: ts,
      complianceConfirmed: false,
      submittedAt: null,
      createdAt: ts,
      updatedAt: ts,
      awardNotifiedAt: null,
      statusHistory: [],
    };
    db.applications.push(app);
    persist();
    return delay(toSectionBasedApplication(app), 200);
  }

  /**
   * Delete an application
   */
  async deleteApplication(applicationId: string): Promise<void> {
    const idx = db.applications.findIndex((a) => a.id === applicationId);
    if (idx === -1) {
      throw new ApplicationServiceError('Postulación no encontrada', 404);
    }
    db.applications.splice(idx, 1);
    persist();
    return delay(undefined, 150);
  }

  /**
   * Download application PDF (stubbed: returns a small placeholder blob)
   */
  async downloadApplicationPdf(
    applicationId: string,
    _isAdmin: boolean = false
  ): Promise<Blob> {
    await delay(null, 200);
    const app = findApp(applicationId);
    const text = `Prototipo Avanza UC\nPostulación: ${
      app?.applicationNumber ?? applicationId
    }\n\n(Este es un PDF de demostración generado por el prototipo estático.)`;
    return new Blob([text], { type: 'application/pdf' });
  }

  /**
   * Get a single application by ID
   */
  async getApplicationById(
    applicationId: string
  ): Promise<SectionBasedApplication> {
    const app = findApp(applicationId);
    if (!app) {
      throw new ApplicationServiceError('Aplicación no encontrada', 404);
    }
    return delay(toSectionBasedApplication(app));
  }

  /**
   * Update application (PATCH)
   */
  async updateApplication(
    applicationId: string,
    updates: {
      answers: {
        sectionAnswers: Record<string, any>;
        sectionStatus: Record<string, SectionStatus>;
      };
    }
  ): Promise<SectionBasedApplication> {
    const app = findApp(applicationId);
    if (!app) {
      throw new ApplicationServiceError('Aplicación no encontrada', 404);
    }
    app.sectionAnswers = {
      ...app.sectionAnswers,
      ...updates.answers.sectionAnswers,
    };
    app.sectionStatus = {
      ...app.sectionStatus,
      ...updates.answers.sectionStatus,
    };
    app.lastSaved = new Date().toISOString();
    app.updatedAt = app.lastSaved;
    persist();
    return delay(toSectionBasedApplication(app), 200);
  }

  /**
   * Submit application
   */
  async submitApplication(
    applicationId: string,
    submitData: {
      answers: {
        applicationNumber: string;
        sectionAnswers: Record<string, any>;
        sectionStatus: Record<string, SectionStatus>;
        status: 'submitted';
        lastSaved: Date;
        complianceConfirmed: boolean;
      };
    }
  ): Promise<SectionBasedApplication> {
    const app = findApp(applicationId);
    if (!app) {
      throw new ApplicationServiceError('Aplicación no encontrada', 404);
    }
    const ts = new Date().toISOString();
    app.sectionAnswers = {
      ...app.sectionAnswers,
      ...submitData.answers.sectionAnswers,
    };
    app.sectionStatus = {
      ...app.sectionStatus,
      ...submitData.answers.sectionStatus,
    };
    app.status = 'submitted';
    app.complianceConfirmed = submitData.answers.complianceConfirmed;
    app.submittedAt = ts;
    app.lastSaved = ts;
    app.updatedAt = ts;
    const user = db.users.find((u) => u.id === app.userId);
    app.statusHistory = [
      ...app.statusHistory,
      {
        status: 'submitted',
        changedBy: app.userId,
        changedByName: user?.name ?? 'Investigador',
        changedAt: ts,
        notes: 'Postulación enviada por el investigador.',
      },
    ];
    persist();
    return delay(toSectionBasedApplication(app), 250);
  }

  /**
   * Get application evaluations (researcher view - no evaluator information)
   */
  async getApplicationEvaluations(
    applicationId: string
  ): Promise<ApplicationWithEvaluations> {
    const app = findApp(applicationId);
    if (!app) {
      throw new ApplicationServiceError('Aplicación no encontrada', 404);
    }
    return delay(toApplicationWithEvaluations(app));
  }
}

const applicationService = new ApplicationService();
export default applicationService;
export { ApplicationServiceError };
